import { Injectable } from '@angular/core'
import { TrackableService } from './trackable.service'
import {
    NeuroglancerAnnotationUserLayer,
    NeuroglancerManagedUserLayer,
    NeuroglancerSegmentationUserLayer,
    NeuroglancerViewer,
} from '../../shared/model/neuroglancer-type-mappings'
import {
    BehaviorSubject,
    combineLatestWith,
    debounceTime,
    distinctUntilChanged,
    filter,
    map,
    Observable,
    of,
    share,
    shareReplay,
    skip,
    Subject,
    switchMap,
    takeUntil,
    // withLatestFrom
} from 'rxjs'
import {
    isAnnotationUserLayer,
    isSegmentationLayer,
    isSegmentationUserLayer,
    NeuroglancerAnnotationRenderLayer,
} from '../../shared/model/layers/layer-types'
import { NeuroglancerConfig } from '../../state/viewer/viewer.state'
import { hasDisplayableNeuroglancerLayers } from '../../state/viewer/viewer-state-utils'
import { Store } from '@ngrx/store'
import { viewerActions } from '../../state/viewer/viewer.actions'
import { selectCaseId } from '../../state/case/case.selectors'
// import { selectCaseId, selectCaseSegObjectPropertyMap} from '../../state/case/case.selectors'

@Injectable({
    providedIn: 'root',
})
export class ViewerService {
    constructor(
        private readonly trackableService: TrackableService,
        private readonly store: Store
    ) {
        const debounceDuration = 200

        this.store
            .select(selectCaseId)
            .pipe(
                switchMap(() =>
                    this.position$.pipe(
                        debounceTime(debounceDuration),
                        skip(1) // This skips the first initial emit caused by loading the viewer
                    )
                )
            )
            .subscribe(pos => {
                if (pos?.length !== 3) return
                this.store.dispatch(
                    viewerActions.setPosition({ position: [pos[0], pos[1], pos[2]] })
                )
            })

        this.store
            .select(selectCaseId)
            .pipe(
                switchMap(() =>
                    this.zoom$.pipe(
                        filter(
                            (zoom: number | null): zoom is number => zoom !== null && !isNaN(zoom)
                        ),
                        debounceTime(debounceDuration),
                        skip(1), // This skips the first initial emit caused by loading the viewer
                        distinctUntilChanged()
                    )
                )
            )
            .subscribe(zoom => {
                this.store.dispatch(viewerActions.setZoom({ zoom }))
            })
    }

    private get viewer(): NeuroglancerViewer | null {
        return this.viewer$.value
    }
    private readonly viewer$ = new BehaviorSubject<NeuroglancerViewer | null>(null)
    private readonly viewerConfig$ = new BehaviorSubject<NeuroglancerConfig | null>(null)
    private readonly disposeViewer$ = new Subject<void>()
    private configSyncGeneration = 0
    private readonly configSyncInProgress$ = new BehaviorSubject(false)

    readonly viewerConfigSyncInProgress$ = this.configSyncInProgress$.asObservable()

    readonly position$ = this.viewer$.pipe(
        switchMap(viewer =>
            viewer ? this.trackableService.observeValue(viewer!.position) : of(null)
        ),
        filter((position): position is Float32Array => position !== null),
        map(position => new Float32Array(position)), // copy
        distinctUntilChanged((prev, curr) => {
            const numberOfDimensions = 3
            return (
                prev.length >= numberOfDimensions &&
                curr.length >= numberOfDimensions &&
                prev.slice(0, numberOfDimensions).every((value, index) => value === curr[index])
            )
        }),
        share()
    )

    readonly zoom$ = this.viewer$.pipe(
        switchMap(viewer =>
            viewer
                ? this.trackableService.observeValue(viewer!.navigationState.zoomFactor)
                : of(null)
        ),
        distinctUntilChanged((prev, curr) => prev === curr),
        share()
    )

    private readonly layersChanged$ = this.viewer$.pipe(
        switchMap(viewer =>
            viewer
                ? this.trackableService.observeEvent(viewer!.layerManager.specificationChanged)
                : of(null)
        )
    )

    readonly layers$ = this.layersChanged$.pipe(
        map(() =>
            this.viewer
                ? this.viewer.layerManager.managedLayers.filter(layer => layer.layer !== null)
                : []
        ),
        distinctUntilChanged(
            (previous, current) =>
                previous.length === current.length &&
                current.every((layer, i) => previous[i] === layer)
        ),
        shareReplay(1)
    )

    startConfigSync(): number {
        const generation = ++this.configSyncGeneration
        this.configSyncInProgress$.next(true)
        return generation
    }

    finishConfigSync(generation: number): void {
        if (generation === this.configSyncGeneration) {
            this.configSyncInProgress$.next(false)
        }
    }

    isConfigSyncInProgress(): boolean {
        return this.configSyncInProgress$.value
    }

    isConfigSyncGenerationCurrent(generation: number): boolean {
        return generation === this.configSyncGeneration
    }

    setViewerConfig(config: NeuroglancerConfig): void {
        this.viewerConfig$.next(config)
    }

    setViewer(viewer: NeuroglancerViewer): void {
        if (this.viewer !== null) {
            throw Error('Viewer cannot be initialized more than once.')
        }

        this.viewerConfig$.pipe(takeUntil(this.disposeViewer$)).subscribe(config => {
            if (config) this.updateViewer(viewer, config)
        })
    }

    disposeViewer(): void {
        this.viewer?.dispose()
        this.disposeViewer$.next()
        this.viewerConfig$.next(null)
        this.viewer$.next(null)
        this.configSyncGeneration = 0
        this.configSyncInProgress$.next(false)
    }

    setPosition(position: Float32Array): void {
        if (this.viewer == null) {
            return
        }

        this.viewer.position.value = position
    }

    centerPosition(): void {
        const coords = this.viewer?.coordinateSpace.value
        if (!coords?.valid) return

        const lower = this.getMeanBounds(coords.boundingBoxes.map(box => box.box.lowerBounds))
        const upper = this.getMeanBounds(coords.boundingBoxes.map(box => box.box.upperBounds))
        const center = new Float32Array([
            (upper[0] - lower[0]) / 2,
            (upper[1] - lower[1]) / 2,
            (upper[2] - lower[2]) / 2,
        ])
        this.setPosition(center)
    }

    toggleLayoutText: 'xy' | 'xz' | 'yz' | undefined
    toggleLayout() {
        this.viewerConfig$.subscribe()
        const upperBoundZ = this.viewer?.coordinateSpace.value.bounds.upperBounds[2] ?? 1
        const lowerBoundZ = this.viewer?.coordinateSpace.value.bounds.lowerBounds[2] ?? 0
        const is2D = upperBoundZ - lowerBoundZ <= 1
        if (is2D) {
            this.toggleLayoutText = 'xy'
            return // 2D layers
        } else {
            switch (this.toggleLayoutText) {
                case 'xy':
                    this.toggleLayoutText = 'xz'
                    break
                case 'xz':
                    this.toggleLayoutText = 'yz'
                    break
                case 'yz':
                    this.toggleLayoutText = 'xy'
                    break
                default:
                    this.toggleLayoutText = this.viewerConfig$.getValue()?.layout // Reset to default if unexpected value
                    break
            }
        }

        this.viewer?.layout.container.setSpecification(this.toggleLayoutText)
        this.store.dispatch(viewerActions.setLayout({ layout: this.toggleLayoutText }))
    }

    getLayout(): 'xy' | 'xz' | 'yz' | undefined {
        if (this.toggleLayoutText == undefined) {
            this.toggleLayoutText = this.viewerConfig$.getValue()?.layout
        }
        return this.toggleLayoutText
    }

    private getMeanBounds(boxes: Float64Array[]): [number, number, number] {
        return boxes
            .reduce(
                (acc, box): [number[], number[], number[]] =>
                    box.length === 3
                        ? [
                              [...acc[0], box[0]],
                              [...acc[1], box[1]],
                              [...acc[2], box[2]],
                          ]
                        : acc,
                [[], [], []] as [number[], number[], number[]]
            )
            .map(dimension =>
                dimension.length > 0 ? dimension.sort()[Math.floor(dimension.length / 2)] : 0
            ) as [number, number, number]
    }

    setZoom(zoom: number): void {
        // for a slightly more advanced idea of "zoom level", as in "nm/px", see:
        // lib/neuroglancer/src/neuroglancer/widget/display_dimensions_widget.ts
        // (in addition, neuroglancer allows different zoom per axis / per viewport)
        if (this.viewer == null) {
            return
        }

        this.viewer.navigationState.zoomFactor.value = zoom
    }

    getSegmentationLayerByName(name: string): NeuroglancerSegmentationUserLayer | null {
        const managedUserLayer = this.getManagedUserLayerByName(name)
        if (!isSegmentationLayer(managedUserLayer)) return null
        return managedUserLayer!.layer as NeuroglancerSegmentationUserLayer
    }

    getAnnotationLayerByName$(name: string): Observable<NeuroglancerAnnotationRenderLayer> {
        return this.layers$.pipe(
            map(layers => layers.find(layer => layer.name === name)?.layer),
            filter((layer): layer is NeuroglancerAnnotationUserLayer =>
                isAnnotationUserLayer(layer)
            ),
            switchMap(layer =>
                of(layer).pipe(
                    combineLatestWith(
                        layer.isReady
                            ? // emit immediately if layer is ready
                              of(null)
                            : // else wait for layer ready state to change
                              this.trackableService.observeEvent(layer.readyStateChanged)
                    )
                )
            ),
            map(([layer]) => {
                // One renderLayer per annotation type (points, lines, ...), first one is points
                // type: AnnotationRenderLayer (can't import that type...?)
                return layer.renderLayers.length > 0
                    ? (layer.renderLayers[0] as NeuroglancerAnnotationRenderLayer)
                    : null
            }),
            filter((layer): layer is NeuroglancerAnnotationRenderLayer => layer !== null),
            share()
        )
    }

    getSegmentationLayerNameBySource$(
        source: string
    ): Observable<NeuroglancerSegmentationUserLayer> {
        return this.layers$.pipe(
            map(
                layers =>
                    layers.find(layer => layer.layer?.dataSources[0]?.spec.url === source)?.layer
            ),
            filter((layer): layer is NeuroglancerSegmentationUserLayer =>
                isSegmentationUserLayer(layer)
            ),
            map(layer => {
                const managedUserLayer = this.getManagedUserLayerByName(layer.managedLayer.name)
                if (!isSegmentationLayer(managedUserLayer)) return null
                return managedUserLayer!.layer as NeuroglancerSegmentationUserLayer
            }),
            filter((layer): layer is NeuroglancerSegmentationUserLayer => layer !== null),
            share()
        )
    }

    getAnnotationLayerNameBySource$(source: string): Observable<string> {
        return this.layers$.pipe(
            map(
                layers =>
                    layers.find(layer => layer.layer?.dataSources[0]?.spec.url === source)?.layer
            ),
            filter((layer): layer is NeuroglancerAnnotationUserLayer =>
                isAnnotationUserLayer(layer)
            ),
            switchMap(layer =>
                of(layer).pipe(
                    combineLatestWith(
                        layer.isReady
                            ? of(null) // emit immediately if layer is ready
                            : this.trackableService.observeEvent(layer.readyStateChanged)
                    )
                )
            ),
            map(([layer]) => layer.managedLayer.name),
            filter((name): name is string => name !== undefined),
            share()
        )
    }

    private getManagedUserLayerByName(name: string): NeuroglancerManagedUserLayer | null {
        if (this.viewer == null) return null
        const managedUserLayer = this.viewer.layerSpecification.layerManager.getLayerByName(name)
        return managedUserLayer?.layer ? managedUserLayer : null
    }

    private updateViewer(viewer: NeuroglancerViewer, viewerConfig: NeuroglancerConfig): void {
        // restoreState creates layers before selectionState is initialized; suspend picking
        // so LayerSelectedValues.handleChange does not run during the two-phase layer restore.
        this.suspendMouseSelection(viewer)

        if (this.viewer === null) {
            viewer.state.restoreState(viewerConfig)
            this.viewer$.next(viewer)
        } else {
            // This prevents a stack overflow ("too much recursion") when
            // many (~1000) layers are active and a different case is loaded.
            this.viewer.state.reset()
            this.viewer.state.restoreState(viewerConfig)
        }

        this.applyEmptyLayerUiPolicy(viewer, viewerConfig)
    }

    configureViewerForExternalLayerManagement(viewer: NeuroglancerViewer): void {
        viewer.showLayerDialog = false
        viewer.resetStateWhenEmpty = false
    }

    private applyEmptyLayerUiPolicy(
        viewer: NeuroglancerViewer,
        viewerConfig: NeuroglancerConfig
    ): void {
        if (hasDisplayableNeuroglancerLayers(viewerConfig.layers)) {
            return
        }

        viewer.showLayerDialog = false
        viewer.selectedLayer.visible = false
    }

    private suspendMouseSelection(viewer: NeuroglancerViewer): void {
        const { mouseState } = viewer
        mouseState.setForcer(undefined)
        if (mouseState.active) {
            mouseState.setActive(false)
        }
    }

    getCoordinate() {
        return Array.prototype.slice.call(this.viewer?.mouseState.position)
    }
}
